import React from 'react';
import { connect } from 'react-redux';
import { withStyles } from 'tss-react/mui';
import { getAvailableDevices } from '../../base/devices/actions.web';
import AbstractDialogTab from '../../base/dialog/components/web/AbstractDialogTab';
import { translate } from '../../base/i18n/functions';
import { createLocalTrack } from '../../base/lib-jitsi-meet/functions.web';
import Checkbox from '../../base/ui/components/web/Checkbox';
import Select from '../../base/ui/components/web/Select';
import { SS_DEFAULT_FRAME_RATE } from '../../settings/constants';
import logger from '../logger';
import DeviceSelector from './DeviceSelector.web';
import VideoInputPreview from './VideoInputPreview';
import { getLocalParticipant } from '../../base/participants/functions';
import { setFlip } from '../../flip/actions';
const styles = (theme) => {
    return {
        container: {
            display: 'flex',
            flexDirection: 'column',
            padding: '0 2px',
            width: '100%'
        },
        checkboxContainer: {
            margin: `${theme.spacing(4)} 0`
        }
    };
};
/**
 * React {@code Component} for previewing audio and video input/output devices.
 *
 * @augments Component
 */
class VideoDeviceSelection extends AbstractDialogTab {
    /**
     * Initializes a new DeviceSelection instance.
     *
     * @param {Object} props - The read-only React Component props with which
     * the new instance is to be initialized.
     */
    constructor(props) {
        super(props);
        this.state = {
            previewVideoTrack: null,
            previewVideoTrackError: null
        };
        this._unMounted = true;
        this._onFramerateItemSelect = this._onFramerateItemSelect.bind(this);
    }
    /**
     * Generate the initial previews for audio input and video input.
     *
     * @inheritdoc
     */
    componentDidMount() {
        this._unMounted = false;
        Promise.all([
            this._createVideoInputTrack(this.props.selectedVideoInputId)
        ])
            .catch(err => logger.warn('Failed to initialize preview tracks', err))
            .then(() => {
            this.props.dispatch(getAvailableDevices());
        });
    }
    /**
     * Checks if audio / video permissions were granted. Updates audio input and
     * video input previews.
     *
     * @param {Object} prevProps - Previous props this component received.
     * @returns {void}
     */
    componentDidUpdate(prevProps) {
        if (prevProps.selectedVideoInputId
            !== this.props.selectedVideoInputId) {
            this._createVideoInputTrack(this.props.selectedVideoInputId);
        }
    }
    /**
     * Ensure preview tracks are destroyed to prevent continued use.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        this._unMounted = true;
        this._disposeVideoInputPreview();
    }
    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     */
    render() {
        const { dispatch, disableLocalVideoFlip, hideAdditionalSettings, hideVideoInputPreview, localFlipX, t, flip, participant } = this.props;
        const classes = withStyles.getClasses(this.props);
        return (React.createElement("div", { className: classes.container },
            !hideVideoInputPreview
                && React.createElement(VideoInputPreview, { error: this.state.previewVideoTrackError, localFlipX: localFlipX, track: this.state.previewVideoTrack }),
            React.createElement("div", { "aria-live": 'polite' }, this._renderVideoSelector()),
            !hideAdditionalSettings && (React.createElement(React.Fragment, null,
                !disableLocalVideoFlip && (React.createElement("div", { className: classes.checkboxContainer },
                    React.createElement(Checkbox, { checked: localFlipX, label: t('videothumbnail.mirrorVideo'), 
                        // eslint-disable-next-line react/jsx-no-bind
                        onChange: () => {
                            if (!participant)
                                return;
                            const isFlipped = flip.participantIds.includes(participant.id);
                            if (!isFlipped) {
                                dispatch(setFlip([...flip.participantIds, participant.id], true));
                            }
                            else {
                                dispatch(setFlip([...flip.participantIds].filter(id => (id != participant.id)), true));
                            }
                        } }))),
                this._renderFramerateSelect()))));
    }
    /**
     * Creates the JitsiTrack for the video input preview.
     *
     * @param {string} deviceId - The id of video device to preview.
     * @private
     * @returns {void}
     */
    _createVideoInputTrack(deviceId) {
        const { hideVideoInputPreview } = this.props;
        if (hideVideoInputPreview) {
            return;
        }
        return this._disposeVideoInputPreview()
            .then(() => createLocalTrack('video', deviceId, 5000))
            .then(jitsiLocalTrack => {
            if (!jitsiLocalTrack) {
                return Promise.reject();
            }
            if (this._unMounted) {
                jitsiLocalTrack.dispose();
                return;
            }
            this.setState({
                previewVideoTrack: jitsiLocalTrack,
                previewVideoTrackError: null
            });
        })
            .catch(() => {
            this.setState({
                previewVideoTrack: null,
                previewVideoTrackError: this.props.t('deviceSelection.previewUnavailable')
            });
        });
    }
    /**
     * Utility function for disposing the current video input preview.
     *
     * @private
     * @returns {Promise}
     */
    _disposeVideoInputPreview() {
        return this.state.previewVideoTrack
            ? this.state.previewVideoTrack.dispose() : Promise.resolve();
    }
    /**
     * Creates a DeviceSelector instance based on the passed in configuration.
     *
     * @private
     * @returns {ReactElement}
     */
    _renderVideoSelector() {
        const { availableDevices, hasVideoPermission } = this.props;
        const videoConfig = {
            devices: availableDevices.videoInput,
            hasPermission: hasVideoPermission,
            icon: 'icon-camera',
            isDisabled: this.props.disableVideoInputSelect || this.props.disableDeviceChange,
            key: 'videoInput',
            id: 'videoInput',
            label: 'settings.selectCamera',
            onSelect: (selectedVideoInputId) => super._onChange({ selectedVideoInputId }),
            selectedDeviceId: this.state.previewVideoTrack
                ? this.state.previewVideoTrack.getDeviceId() : this.props.selectedVideoInputId
        };
        return (React.createElement(DeviceSelector, { ...videoConfig, key: videoConfig.id }));
    }
    /**
     * Callback invoked to select a frame rate from the select dropdown.
     *
     * @param {Object} e - The key event to handle.
     * @private
     * @returns {void}
     */
    _onFramerateItemSelect(e) {
        const frameRate = e.target.value;
        super._onChange({ currentFramerate: frameRate });
    }
    /**
     * Returns the React Element for the desktop share frame rate dropdown.
     *
     * @returns {JSX}
     */
    _renderFramerateSelect() {
        const { currentFramerate, desktopShareFramerates, t } = this.props;
        const frameRateItems = desktopShareFramerates.map((frameRate) => {
            return {
                value: frameRate,
                label: `${frameRate} ${t('settings.framesPerSecond')}`
            };
        });
        return (React.createElement(Select, { bottomLabel: parseInt(currentFramerate, 10) > SS_DEFAULT_FRAME_RATE
                ? t('settings.desktopShareHighFpsWarning')
                : t('settings.desktopShareWarning'), id: 'more-framerate-select', label: t('settings.desktopShareFramerate'), onChange: this._onFramerateItemSelect, options: frameRateItems, value: currentFramerate }));
    }
}
const mapStateToProps = (state) => {
    return {
        participant: getLocalParticipant(state),
        flip: state['features/flip'],
        availableDevices: state['features/base/devices'].availableDevices ?? {}
    };
};
export default connect(mapStateToProps)(withStyles(translate(VideoDeviceSelection), styles));
